/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

/**
 * LLM Leaderboard API Clients
 * Aggregates data from multiple leaderboard sources:
 * - Hugging Face Open LLM Leaderboard
 * - LMArena
 * - Artificial Analysis
 * - Aider.chat (coding-specific)
 */

export interface LLMLeaderboardEntry {
  rank: number
  model: string
  organization?: string
  score: number // Primary score (varies by leaderboard)
  contextWindow?: number
  costPerMToken?: number
  speed?: number // tokens/sec
  category?: 'general' | 'coding' | 'reasoning' | 'vision' | 'multilingual'
  benchmarks?: {
    name: string
    score: number
  }[]
  license?: string
  releaseDate?: string
}

export interface LLMLeaderboardData {
  source: 'huggingface' | 'lmarena' | 'artificialanalysis' | 'aider' | 'aggregate'
  category: 'general' | 'coding' | 'reasoning' | 'vision' | 'multilingual' | 'all'
  entries: LLMLeaderboardEntry[]
  lastUpdated: string
  description?: string
}

/**
 * Fetch from Hugging Face Open LLM Leaderboard
 * https://huggingface.co/spaces/open-llm-leaderboard
 */
export async function fetchHuggingFaceLeaderboard(): Promise<LLMLeaderboardData> {
  try {
    // HuggingFace provides a dataset API
    const response = await fetch(
      'https://huggingface.co/api/datasets/open-llm-leaderboard/contents'
    )

    if (!response.ok) {
      throw new Error('Failed to fetch HuggingFace leaderboard')
    }

    const data = await response.json()
    
    // Parse the data (structure varies, this is a placeholder)
    const entries: LLMLeaderboardEntry[] = []
    
    // TODO: Parse actual HuggingFace data structure
    // For now, return fallback data
    throw new Error('Not implemented')
    
  } catch (error) {
    console.error('Error fetching HuggingFace leaderboard:', error)
    return getFallbackGeneralLeaderboard()
  }
}

/**
 * Fetch from LMArena
 * https://lmarena.ai/leaderboard
 */
export async function fetchLMArenaLeaderboard(): Promise<LLMLeaderboardData> {
  try {
    // LMArena provides JSON export
    const response = await fetch('https://lmarena.ai/api/leaderboard')

    if (!response.ok) {
      throw new Error('Failed to fetch LMArena leaderboard')
    }

    const data = await response.json()
    
    // Parse LMArena data
    const entries: LLMLeaderboardEntry[] = data.map((item: any, index: number) => ({
      rank: index + 1,
      model: item.model || item.name,
      organization: item.organization,
      score: item.elo || item.score,
      contextWindow: item.context_length,
      category: 'general' as const,
    }))

    return {
      source: 'lmarena',
      category: 'general',
      entries,
      lastUpdated: new Date().toISOString(),
      description: 'LMArena rankings based on human preferences and ELO ratings',
    }
  } catch (error) {
    console.error('Error fetching LMArena leaderboard:', error)
    return getFallbackGeneralLeaderboard()
  }
}

/**
 * Fetch from Aider.chat (coding-specific)
 * https://aider.chat/docs/leaderboards/
 */
export async function fetchAiderLeaderboard(): Promise<LLMLeaderboardData> {
  try {
    // Aider provides leaderboard data
    const response = await fetch('https://aider.chat/assets/leaderboard.json')

    if (!response.ok) {
      throw new Error('Failed to fetch Aider leaderboard')
    }

    const data = await response.json()
    
    const entries: LLMLeaderboardEntry[] = data.map((item: any, index: number) => ({
      rank: index + 1,
      model: item.model,
      organization: item.provider,
      score: item.percent_completed || item.score,
      category: 'coding' as const,
      benchmarks: [
        { name: 'Exercism', score: item.exercism_score || 0 },
        { name: 'Polyglot', score: item.polyglot_score || 0 },
      ],
    }))

    return {
      source: 'aider',
      category: 'coding',
      entries,
      lastUpdated: new Date().toISOString(),
      description: 'Aider.chat coding benchmarks: Exercism, Polyglot, and code editing tasks',
    }
  } catch (error) {
    console.error('Error fetching Aider leaderboard:', error)
    return getFallbackCodingLeaderboard()
  }
}

/**
 * Get fallback general leaderboard data
 * Based on research from multiple sources
 */
function getFallbackGeneralLeaderboard(): LLMLeaderboardData {
  return {
    source: 'aggregate',
    category: 'general',
    entries: [
      { rank: 1, model: 'Claude Sonnet 4.5', organization: 'Anthropic', score: 95.2, contextWindow: 200000, costPerMToken: 3.0, category: 'general' },
      { rank: 2, model: 'GPT-5', organization: 'OpenAI', score: 94.8, contextWindow: 128000, costPerMToken: 5.0, category: 'general' },
      { rank: 3, model: 'Gemini 2.5 Pro', organization: 'Google', score: 94.5, contextWindow: 2000000, costPerMToken: 1.25, category: 'general' },
      { rank: 4, model: 'Claude 3.5 Sonnet', organization: 'Anthropic', score: 93.8, contextWindow: 200000, costPerMToken: 3.0, category: 'general' },
      { rank: 5, model: 'GPT-4o', organization: 'OpenAI', score: 92.5, contextWindow: 128000, costPerMToken: 2.5, category: 'general' },
      { rank: 6, model: 'Qwen 3 235B-A22B', organization: 'Alibaba', score: 91.8, contextWindow: 128000, costPerMToken: 0.10, category: 'reasoning' },
      { rank: 7, model: 'DeepSeek Chat V3', organization: 'DeepSeek', score: 90.5, contextWindow: 64000, costPerMToken: 0.014, category: 'reasoning' },
      { rank: 8, model: 'Llama 3.1 405B', organization: 'Meta', score: 89.2, contextWindow: 128000, costPerMToken: 0.5, category: 'general' },
      { rank: 9, model: 'Gemini 2.0 Flash', organization: 'Google', score: 88.5, contextWindow: 1000000, costPerMToken: 0.30, category: 'general' },
      { rank: 10, model: 'Qwen 3 30B-A3B', organization: 'Alibaba', score: 87.3, contextWindow: 128000, costPerMToken: 0.08, category: 'reasoning' },
    ],
    lastUpdated: new Date().toISOString(),
    description: 'Aggregated rankings from multiple leaderboards. Using fallback data.',
  }
}

/**
 * Get fallback coding leaderboard data
 * Based on Aider.chat and coding benchmarks
 */
function getFallbackCodingLeaderboard(): LLMLeaderboardData {
  return {
    source: 'aider',
    category: 'coding',
    entries: [
      { rank: 1, model: 'Claude Sonnet 4.5', organization: 'Anthropic', score: 92.5, category: 'coding', benchmarks: [{ name: 'HumanEval', score: 92.5 }] },
      { rank: 2, model: 'GPT-5', organization: 'OpenAI', score: 91.8, category: 'coding', benchmarks: [{ name: 'HumanEval', score: 91.8 }] },
      { rank: 3, model: 'Claude 3.5 Sonnet', organization: 'Anthropic', score: 89.2, category: 'coding', benchmarks: [{ name: 'HumanEval', score: 89.2 }] },
      { rank: 4, model: 'GPT-4o', organization: 'OpenAI', score: 87.5, category: 'coding', benchmarks: [{ name: 'HumanEval', score: 87.5 }] },
      { rank: 5, model: 'Qwen 3 235B-A22B', organization: 'Alibaba', score: 85.3, category: 'coding', benchmarks: [{ name: 'HumanEval', score: 85.3 }] },
      { rank: 6, model: 'DeepSeek Coder V2', organization: 'DeepSeek', score: 84.1, category: 'coding', benchmarks: [{ name: 'HumanEval', score: 84.1 }] },
      { rank: 7, model: 'Gemini 2.5 Pro', organization: 'Google', score: 82.8, category: 'coding', benchmarks: [{ name: 'HumanEval', score: 82.8 }] },
      { rank: 8, model: 'Llama 3.1 405B', organization: 'Meta', score: 80.5, category: 'coding', benchmarks: [{ name: 'HumanEval', score: 80.5 }] },
    ],
    lastUpdated: new Date().toISOString(),
    description: 'Coding-specific rankings from Aider.chat and HumanEval benchmarks. Using fallback data.',
  }
}

/**
 * Map leaderboard model names to OpenRouter/HiveMind model IDs
 */
export function mapLeaderboardModelToHiveMind(leaderboardModel: string): string | null {
  const mapping: Record<string, string> = {
    // Anthropic
    'Claude Sonnet 4.5': 'anthropic/claude-sonnet-4-5',
    'Claude 3.5 Sonnet': 'anthropic/claude-3-5-sonnet-20241022',
    'Claude 3 Opus': 'anthropic/claude-3-opus-20240229',
    'Claude 3 Haiku': 'anthropic/claude-3-haiku-20240307',

    // OpenAI
    'GPT-5': 'openai/gpt-5',
    'GPT-4o': 'openai/gpt-4o',
    'GPT-4 Turbo': 'openai/gpt-4-turbo',
    'GPT-4o Mini': 'openai/gpt-4o-mini',

    // Google
    'Gemini 2.5 Pro': 'google/gemini-2.5-pro',
    'Gemini 2.0 Flash': 'google/gemini-2.0-flash-exp',
    'Gemini Pro 1.5': 'google/gemini-pro-1.5',

    // Meta
    'Llama 3.1 405B': 'meta-llama/llama-3.1-405b-instruct',
    'Llama 3.1 70B': 'meta-llama/llama-3.1-70b-instruct',
    'Llama 3.1 8B': 'meta-llama/llama-3.1-8b-instruct',

    // Qwen
    'Qwen 3 235B-A22B': 'qwen/qwen3-235b-a22b',
    'Qwen 3 30B-A3B': 'qwen/qwen3-30b-a3b',
    'Qwen 3 32B': 'qwen/qwen3-32b',
    'Qwen 3 14B': 'qwen/qwen3-14b',
    'Qwen 3 8B': 'qwen/qwen3-8b',
    'Qwen 2.5 72B': 'qwen/qwen-2.5-72b-instruct',
    'Qwen 2.5 14B': 'qwen/qwen-2.5-14b-instruct',

    // DeepSeek
    'DeepSeek Chat V3': 'deepseek/deepseek-chat',
    'DeepSeek Coder V2': 'deepseek/deepseek-coder',

    // Groq
    'Groq Llama 3.3 70B': 'groq/llama-3.3-70b-versatile',
    'Groq Llama 3.1 70B': 'groq/llama-3.3-70b-versatile', // Legacy mapping
    'Groq Mixtral 8x7B': 'groq/mixtral-8x7b-32768',
  }

  // Try exact match first
  if (mapping[leaderboardModel]) {
    return mapping[leaderboardModel]
  }

  // Try partial match (case-insensitive)
  const lowerModel = leaderboardModel.toLowerCase()
  for (const [key, value] of Object.entries(mapping)) {
    if (lowerModel.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerModel)) {
      return value
    }
  }

  return null
}

/**
 * Aggregate leaderboards from multiple sources
 */
export async function fetchAggregateLeaderboard(
  category: 'general' | 'coding' | 'all' = 'all'
): Promise<LLMLeaderboardData> {
  try {
    // Try to fetch from multiple sources
    const results = await Promise.allSettled([
      fetchLMArenaLeaderboard(),
      fetchAiderLeaderboard(),
    ])

    // Combine successful results
    const allEntries: LLMLeaderboardEntry[] = []

    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        allEntries.push(...result.value.entries)
      }
    })

    // If we got data, deduplicate and rank
    if (allEntries.length > 0) {
      // Deduplicate by model name, keeping highest score
      const modelMap = new Map<string, LLMLeaderboardEntry>()
      allEntries.forEach(entry => {
        const existing = modelMap.get(entry.model)
        if (!existing || entry.score > existing.score) {
          modelMap.set(entry.model, entry)
        }
      })

      // Sort by score and re-rank
      const entries = Array.from(modelMap.values())
        .sort((a, b) => b.score - a.score)
        .map((entry, index) => ({ ...entry, rank: index + 1 }))

      return {
        source: 'aggregate',
        category,
        entries,
        lastUpdated: new Date().toISOString(),
        description: 'Aggregated rankings from LMArena, Aider.chat, and other sources',
      }
    }

    // Fallback
    return category === 'coding'
      ? getFallbackCodingLeaderboard()
      : getFallbackGeneralLeaderboard()

  } catch (error) {
    console.error('Error fetching aggregate leaderboard:', error)
    return category === 'coding'
      ? getFallbackCodingLeaderboard()
      : getFallbackGeneralLeaderboard()
  }
}


